<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, shallowRef, triggerRef } from 'vue';
import { useRouter } from 'vue-router';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { vMaska } from "maska/vue"
import { useDictionaryStore } from '@/store/dictionary';
import {
  YandexMap,
  YandexMapDefaultSchemeLayer,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultMarker,
  YandexMapListener,
  YandexMapControls,
  YandexMapGeolocationControl,
  YandexMapOpenMapsButton,
  YandexMapScaleControl,
  YandexMapZoomControl,
  YandexMapEntity
} from 'vue-yandex-maps';
import type { DomEventHandler, YMap } from '@yandex/ymaps3-types';
import type { YMapDefaultMarker } from '@yandex/ymaps3-types/packages/markers';
import { useToast } from '@/components/ui/toast'
import Input from '@/components/ui/input/Input.vue';
import Button from '@/components/ui/button/Button.vue';
import SchedulePicker from '@/components/custom/SchedulePicker.vue';
import { getFormattedAddress } from '@/utils/geocoder';
import { Partner } from '@/types';


const router = useRouter();
const dictionaryStore = useDictionaryStore();
const { toast } = useToast()

// Partner data with defaults
const partner = reactive<Partner>({
  name: '',
  country: null,
  city: null,
  address: '',
  launchDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
  contacts: [
    {
      id: crypto.randomUUID(),
      name: '',
      phone: '',
      position: ''
    }
  ],
  menuPackage: '',
  mutualSettlements: '',
  coordinates: {
    latitude: null,
    longitude: null
  },
  schedule: [],
  additionalInfo: ''
});

// Helper for adding/removing contacts
const addContact = () => {
  partner.contacts.push({
    id: crypto.randomUUID(),
    name: '',
    phone: '',
    position: ''
  });
};

const removeContact = (index: number) => {
  if (partner.contacts.length > 1) {
    partner.contacts.splice(index, 1);
  }
};

// Form validation
const isFormValid = computed(() => {
  const requiredFields = [
    partner.name,
    partner.country,
    partner.city,
    partner.launchDate,
    partner.menuPackage,
    partner.mutualSettlements
  ];

  const hasValidContacts = partner.contacts.every(contact =>
    contact.name && contact.phone && contact.position
  );

  // For the MVP version, make coordinates optional
  const hasValidCoordinates = true; // Removed coordinate validation requirement

  return requiredFields.every(field => !!field) && hasValidContacts && hasValidCoordinates;
});

const map = shallowRef<null | YMap>(null);
const defaultMarker = shallowRef<YMapDefaultMarker | null>(null);

onMounted(() => {

});

watch(
  () => partner.city,
  (newValue, oldValue) => {
    if(map.value && newValue) {
      const city = dictionaryStore.dictionary.cities.find((city) => city.id === newValue);
      map.value?.setLocation({
        center: [city?.location.latitude, city?.location.longitude],
        zoom: city?.location.zoom_level
      });
      defaultMarker.value?.update({
        coordinates: [city?.location.latitude, city?.location.longitude],
      });
    }
  }
);


const onDragMove = () => {
    triggerRef(defaultMarker);
};

const handleMapClick: DomEventHandler = (object, event) => {
    const coords = event.coordinates;
    defaultMarker.value?.update({
        coordinates: coords,
    });
};

const getAddressFromCoordinates = () => {
    if(map.value && defaultMarker.value) {
        const coords = defaultMarker.value.coordinates;
        getFormattedAddress(coords[1], coords[0]).then((address) => {
          if( address ) {
            partner.address = address;
          } else {
            toast({
              title: 'Адрес не найден',
              variant: 'destructive'
            });
          }
        });
    }
};

const availableCities = computed(() => {
  if (!partner.country) return [];
  return dictionaryStore.dictionary.cities?.filter((city) => city.country_id === partner.country);
});
const menuPackages = ['Базовый', 'Стандартный', 'Премиум', 'Кастомный'];

// Handle form submission
const isSubmitting = ref(false);

const handleSubmit = async () => {
  if (!isFormValid.value) return;

  try {
    isSubmitting.value = true;

    // Here you would send data to your API
    // await api.partners.create(partner);

    // Navigate back to partners list after successful creation
    router.push('/partners');
  } catch (error) {
    console.error('Failed to create partner:', error);
  } finally {
    isSubmitting.value = false;
  }
};

// Handle cancel/back
const handleCancel = () => {
  router.push('/partners');
};
</script>

<template>
  <div class="container mx-auto py-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-semibold">Создать партнера</h2>
      <div class="hidden md:flex gap-4">
        <Button @click="handleCancel" variant="outline">Отменить</Button>
        <Button @click="handleSubmit" :disabled="!isFormValid || isSubmitting">
          {{ isSubmitting ? 'Создание...' : 'Создать партнера' }}
        </Button>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <!-- Left column - Basic info -->
      <div class="space-y-6">
        <div class="bg-white rounded-lg p-6 shadow-sm border">
          <h3 class="text-lg font-medium mb-4">Базовая информация</h3>
          <div class="space-y-4">
            <div class="space-y-2">
              <label class="block text-sm font-medium">Название партнера *</label>
              <Input v-model="partner.name" placeholder="Введите название партнера" />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium">Страна *</label>
              <Select v-model="partner.country">
                <SelectTrigger>
                  <SelectValue placeholder="Выберите страну" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Страны</SelectLabel>
                    <SelectItem v-for="country in dictionaryStore.dictionary.countries" :key="country.id" :value="country.id">
                      {{ country.name }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium">Город *</label>
              <Select v-model="partner.city">
                <SelectTrigger>
                  <SelectValue placeholder="Выберите город" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Города</SelectLabel>
                    <SelectItem v-for="city in availableCities" :key="city.id" :value="city.id">
                      {{ city.name }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium">Адрес *</label>
              <div class="flex w-full items-center gap-1.5">
                <Input v-model="partner.address" placeholder="Введите адрес" class="flex-grow"/>
                <Button type="button" variant="outline" class="whitespace-nowrap flex-shrink-0" @click="getAddressFromCoordinates">
                  Из координат
                </Button>
              </div>
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium">Дата запуска *</label>
              <Input
                v-model="partner.launchDate"
                type="date"
                class="w-full p-2 bg-white border rounded-md"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium">Взаиморасчет *</label>
              <Select v-model="partner.mutualSettlements">
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите тип платежа" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Тип платежа</SelectLabel>
                      <SelectItem v-for="interval in dictionaryStore.dictionary.payment_intervals" :key="interval.value" :value="interval.value">
                        {{ $t(`payment_intervals.${interval.value}`) }}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
            </div>

            <div class="space-y-2">
              <label class="block text-sm font-medium">Примечание</label>
              <Input
                v-model="partner.additionalInfo"
                placeholder="Введите примечание"
              />
            </div>

          </div>
        </div>

        <div class="bg-white rounded-lg p-6 shadow-sm border">
          <h3 class="text-lg font-medium mb-4">Местоположение *</h3>

          <div class="space-y-4">
            <!-- Yandex Maps integration -->
            <div class="w-full h-[300px] border rounded-md flex items-center justify-center bg-gray-100">
              <yandex-map
                v-model="map"
                :settings="{
                  location: {
                    center: [76.952539, 43.218606],
                    zoom: 11,
                  },
                }"
              >
                <yandex-map-default-scheme-layer/>
                <yandex-map-default-features-layer/>
                <yandex-map-listener :settings="{ onClick: handleMapClick }" />
                <yandex-map-default-marker
                  v-model="defaultMarker"
                  :settings="{
                    coordinates: defaultMarker ? defaultMarker.coordinates : [76.952539, 43.218606],
                    title: `Долгота: ${ defaultMarker?.coordinates[0].toFixed(6) }<br>Широта: ${ defaultMarker?.coordinates[1].toFixed(6) }`,
                    draggable: true,
                    onDragMove,
                  }"
                />
                <yandex-map-controls :settings="{ position: 'left', orientation: 'vertical' }">
                  <yandex-map-zoom-control/>
                </yandex-map-controls>
                <yandex-map-controls :settings="{ position: 'top right' }">
                  <yandex-map-geolocation-control/>
                </yandex-map-controls>
                <yandex-map-controls :settings="{ position: 'bottom right', orientation: 'vertical' }">
                  <yandex-map-open-maps-button/>
                </yandex-map-controls>
              </yandex-map>
            </div>
            <p class="text-sm text-gray-500">
              Выберите координаты местоположения заведения
            </p>
          </div>
        </div>

        <div class="bg-white rounded-lg p-6 shadow-sm border">
          <h3 class="text-lg font-medium mb-4">Режим работы *</h3>
          <div class="space-y-2">
            <SchedulePicker v-model="partner.schedule" />
          </div>
        </div>
      </div>

      <!-- Right column - Contacts and Map -->
      <div class="space-y-6">
        <div class="bg-white rounded-lg p-6 shadow-sm border">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium">Контакты *</h3>
            <Button
              @click="addContact"
              variant="outline"
              size="sm"
              class="text-sm"
            >
              Добавить контакт
            </Button>
          </div>

          <div v-for="(contact, index) in partner.contacts" :key="contact.id" class="border-b pb-4 mb-4 last:border-b-0 last:mb-0 last:pb-0">
            <div class="flex justify-between items-center mb-2">
              <h4 class="font-medium">Контакт #{{ index + 1 }}</h4>
              <button
                v-if="partner.contacts.length > 1"
                @click="removeContact(index)"
                class="text-red-500 hover:text-red-700 text-sm"
                type="button"
              >
                Удалить
              </button>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="space-y-2">
                <label class="block text-sm font-medium">Имя</label>
                <Input v-model="contact.name" placeholder="Имя" />
              </div>

              <div class="space-y-2">
                <label class="block text-sm font-medium">Телефон</label>
                <Input :disabled="!partner.country" v-model="contact.phone" placeholder="Телефон" v-maska="dictionaryStore.dictionary.countries?.find((country) => country.id === partner.country)?.phone_mask" />
              </div>

              <div class="space-y-2 md:col-span-2">
                <label class="block text-sm font-medium">Позиция</label>
                <Select v-model="contact.position">
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите позицию" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Позиция</SelectLabel>
                      <SelectItem v-for="position in dictionaryStore.dictionary.employee_positions" :key="position.value" :value="position.value">
                        {{ $t(`employee_positions.${position.value}`) }}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div v-if="false" class="bg-white rounded-lg p-6 shadow-sm border">
          <h3 class="text-lg font-medium mb-4">Пакет меню</h3>
          <div class="space-y-4">
            <div class="space-y-2">
              <label class="block text-sm font-medium">Пакет меню *</label>
              <!-- <SelectRoot v-model="partner.menuPackage">
                <SelectTrigger class="w-full p-2 bg-white border rounded-md flex justify-between items-center">
                  <SelectValue placeholder="Выберите пакет меню" />
                </SelectTrigger>
                <SelectContent class="bg-white border rounded-md shadow-md z-50 overflow-hidden">
                  <SelectGroup>
                    <SelectItem
                      v-for="pkg in menuPackages"
                      :key="pkg"
                      :value="pkg"
                      class="p-2 hover:bg-gray-100 cursor-pointer">
                      {{ pkg }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </SelectRoot> -->
            </div>
          </div>
        </div>

        <div class="flex md:hidden gap-4 justify-center">
            <Button @click="handleCancel" variant="outline">Отменить</Button>
            <Button @click="handleSubmit" :disabled="!isFormValid || isSubmitting">
              {{ isSubmitting ? 'Создание...' : 'Создать партнера' }}
            </Button>
          </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Add any custom styles here */
:deep(.ymaps-2-1-79-map) {
  width: 100%;
  height: 100%;
}

/* Add some responsive adjustments */
@media (max-width: 768px) {
  .container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
